import { CommonModule } from '@angular/common';
import {
	Component,
	computed,
	effect,
	inject,
	input,
	Signal,
	signal,
} from '@angular/core';

import { TranslateService } from '@ngx-translate/core';
import { DynamicModule } from 'ng-dynamic-component';
import { take } from 'rxjs';

import { CtaType } from '@valk-nx/compositions/ui-persuade-card/src/lib/persuade-card.interface';
import {
	fallbackLanguage,
	PRICING_TOOLTIP_OCCUPANCY,
} from '@valk-nx/core/lib/core';
import { StoryblokSearchableListSectionComponent } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section';
import { ExtendedPersuadeCardInputs } from '@valk-nx/storyblok/components/searchable-list-section/src/lib/searchable-list-section.interface';
import { StoryblokRootDirective } from '@valk-nx/storyblok-directives/src/lib/directives/storyblok-root.directive';
import { ThemePackageData } from '@valk-nx/storyblok-services/src/lib/services/content.interface';
import { ContentService } from '@valk-nx/storyblok-services/src/lib/services/content.service';
import { ISbStoryBlok } from '@valk-nx/storyblok-types/src/lib/types/storyblok.types';

import { StoryblokPackagesEventsListPluginData } from './packages-events-list.interface';

@Component({
	selector: 'sb-packages-events-list',
	templateUrl: './packages-events-list.html',
	imports: [
		CommonModule,
		DynamicModule,
		StoryblokSearchableListSectionComponent,
	],
})
export class StoryblokPackagesEventsListComponent extends StoryblokRootDirective {
	private readonly contentService = inject(ContentService);
	private readonly translate = inject(TranslateService);

	title = input.required<ISbStoryBlok[]>();
	filters = input.required<StoryblokPackagesEventsListPluginData>();
	hideFilter = input<boolean>(false);

	packageEventData = signal<ThemePackageData[] | undefined>([]);

	packageEventItems: Signal<ExtendedPersuadeCardInputs[] | null> =
		signal(null);
	language: string = fallbackLanguage;
	effectRan = false;

	constructor() {
		super();

		this.language =
			this.translate.getCurrentLang() ?? this.translate.getFallbackLang();

		this.packageEventItems = computed(() => {
			if (this.packageEventData().length === 0) return null;

			const mappedPackageEventItems = this.packageEventData()?.map(
				(item) => {
					return {
						images: item.images,
						title: item.name,
						overline: item.hotelName,
						price: item.price,
						pricingToolTip: {
							occupancy: PRICING_TOOLTIP_OCCUPANCY,
						},
						content: item.bulletContent,
						ctaType: 'btn-primary' as CtaType,
						ctaUrl: item.toUrl,
						ctaText: this.translate.instant(
							'storyblok.package-event.cta',
						),
						ctaTarget: '_blank' as const,
						showDisabledText: false,
					};
				},
			);

			return mappedPackageEventItems;
		});

		effect(() => {
			this.filters();

			if (!this.effectRan) {
				this.runEffect();
			}
		});
	}

	runEffect = () => {
		const filterData = this.filters();
		const themeGuids = filterData.themes.map((theme) => theme.guid);
		const hotelGuids = filterData.hotels.map((hotel) => hotel.guid);

		this.contentService
			.getThemePackages(themeGuids, hotelGuids, this.language)
			.pipe(take(1))
			.subscribe((response: { data: ThemePackageData[] }) => {
				this.packageEventData.set(response.data);
			});

		this.effectRan = true;
	};
}
